import React, { useState, useEffect } from "react";
import {
  Button,
  useDisclosure,
  useToast,
  Flex,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
} from "@chakra-ui/react";
import { FaBars, FaPen, FaTrash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Page } from "../../components/Page";
import { Card } from "../../components/Card";
import DataTableV2 from "../../components/DataTable/DateTableV2";
import { TemplateModal } from "./components/TemplateModal";
import {
  useDeleteAssigneeTemplate,
  useGetAssigneeTemplates,
  useGetDimensions,
  useUpdateAssigneeTemplate,
} from "../../hooks/query/dimensions";
import { dimensionsKeys, showErrorToast } from "../../utils";
import { useGetOrganizationUsers } from "../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";

function TaskAssigneesTemplate() {
  const { t } = useTranslation("translation", {
    keyPrefix: "taskAssigneesTemplate",
  });

  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateModalData, setTemplateModalData] = useState(null);

  const { data: dimensionsData, isLoading: dimensionsLoading } = useGetDimensions(
    {},
    {
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
    }
  );

  const { isLoading: templatesLoading } = useGetAssigneeTemplates({
    onSuccess: (data) => {
      setTemplates(data);
    },
    cacheTime: 0,
  });

  // Fetch organization users
  const { data: users, isLoading: usersLoading } = useGetOrganizationUsers({
    onError: (err) => {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
    cacheTime: 0,
  });

  const { mutate: updateAssgineeTemplate } = useUpdateAssigneeTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      dispatch(hideAppSpinner());
      queryClient.invalidateQueries(dimensionsKeys.assigneeTemplates);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  const { mutate: deleteAssigneeTemplate } = useDeleteAssigneeTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      dispatch(hideAppSpinner());
      queryClient.invalidateQueries(dimensionsKeys.assigneeTemplates);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  useEffect(() => {
    if (templatesLoading || usersLoading || dimensionsLoading) {
      dispatch(showAppSpinner());
    } else {
      dispatch(hideAppSpinner());
    }
  }, [dispatch, templatesLoading, usersLoading, dimensionsLoading]);

  const handleCreateTemplate = () => {
    setTemplateModalData(null);
    onOpen();
  };

  const handleTemplateSubmit = (templateId, templateData) => {
    if (templateId) {
      updateAssgineeTemplate({
        id: templateId,
        ...templateData,
      });
    } else {
      updateAssgineeTemplate(templateData);
    }

    onClose();
  };

  const columns = React.useMemo(() => {
    const handleEditTemplate = (templateId) => {
      const template = templates.find((t) => t.id === templateId);
      setTemplateModalData(template);
      onOpen();
    };

    const handleDeleteTemplate = (templateId) => {
      deleteAssigneeTemplate(templateId);
    };

    const dynamicColumns = Object.keys(dimensionsData || {})
      .filter((key) => dimensionsData[key]) // Include only existing dimensions
      .map((key) => ({
        // key is like "dimensionOne", "dimensionTwo", etc.
        id: key,
        header: dimensionsData[key].name, // Use the dimension name as the header
        // accessorKey: `dimensionItems.${key}Item`,
        cell: ({ row }) => {
          const dimension = dimensionsData[key];
          const itemId = row.original.dimensionItems[`${key}Item`];
          const item = dimension.items.find((item) => item.id === itemId);
          return item ? item.name : "All"; // Display the name of the dimension item
        },
      }));

    return [
      {
        id: "name",
        header: t("name"),
        accessorKey: "name",
      },
      ...dynamicColumns,
      {
        id: "assignee",
        header: t("assignee"),
        // accessorKey: "assigneeName",
        cell: ({ row }) => {
          const assigneeId = row.original.assigneeId;
          const assignee = users?.find((user) => user.id === assigneeId);
          return assignee ? assignee.email : "-";
        },
      },
      {
        id: "actions",
        header: t("actions"),
        accessorKey: "actions",
        enableSorting: false,
        cell: ({ row }) => (
          <Menu>
            <MenuButton as={IconButton} aria-label="Options" icon={<FaBars />} variant="outline" size="sm" />
            <MenuList>
              <MenuItem icon={<FaPen />} onClick={() => handleEditTemplate(row.original.id)}>
                {t("edit")}
              </MenuItem>
              <MenuDivider />
              <MenuItem icon={<FaTrash />} onClick={() => handleDeleteTemplate(row.original.id)}>
                {t("delete")}
              </MenuItem>
            </MenuList>
          </Menu>
        ),
      },
    ];
  }, [deleteAssigneeTemplate, dimensionsData, onOpen, t, templates, users]);

  return (
    <Page title={t("pageTitle")}>
      <Card>
        <Flex justifyContent="flex-end" mb={4}>
          <Button colorScheme="customScheme" onClick={handleCreateTemplate}>
            {t("createTemplate")}
          </Button>
        </Flex>

        <DataTableV2 columns={columns} data={templates} enableSorting={true} />
      </Card>

      {isOpen && (
        <TemplateModal
          modalData={templateModalData}
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleTemplateSubmit}
        />
      )}
    </Page>
  );
}

export default TaskAssigneesTemplate;
